/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetMarketBackerEarningsRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { BackerFeeClaimEventSchema, MarketBackingSchema, MarketTradeSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';


// ─── useGetMarketBackerEarnings ────────────────────────────────────────────────────────────

export interface useGetMarketBackerEarningsArgs { readonly pubkey: string; readonly wallet?: string; readonly rewardScope?: string; readonly walletPubkeysCsv?: string; readonly fields?: readonly ('marketPubkey' | 'userWallet' | 'vaultD6' | 'supplyD6' | 'totalCollectedD6' | 'userShareD6' | 'earnedD6' | 'claimedTotalD6' | 'canClaim' | 'marketState')[]; }


async function _useGetMarketBackerEarningsQueryFn(
  args: useGetMarketBackerEarningsArgs,
  ctx: QueryFunctionContext,
): Promise<GetMarketBackerEarningsRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/backer-earnings/${args.pubkey}`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  if (args.wallet !== undefined) params.set('wallet', String(args.wallet));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketBackerEarnings',
    routeTemplate: '/v1/backer-earnings/{pubkey}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetMarketBackerEarningsRow;
}


const _useGetMarketBackerEarningsFactory = Object.assign(
  (args: useGetMarketBackerEarningsArgs) => ({
    queryKey: ['backer-rewards', 'viewer', 'unbound', args.pubkey] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketBackerEarningsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: 'always' as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarketBackerEarnings',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['backer-rewards', 'viewer', 'unbound', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketBackerEarningsArgs, GetMarketBackerEarningsRow>;


export const useGetMarketBackerEarningsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/backer-earnings/{pubkey}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetMarketBackerEarningsFactory },
  parseInput: (raw: unknown): useGetMarketBackerEarningsArgs => raw as useGetMarketBackerEarningsArgs,
  parseResponse: (raw: unknown): GetMarketBackerEarningsRow => raw as GetMarketBackerEarningsRow,
  specName: 'useGetMarketBackerEarnings' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — the detail hook reads and writes the same canonical entity as
  // every normalized market list. The completeness guard above still forces
  // a detail fetch when this key currently holds only the narrower list DTO.
  canonicalEntity: true as const,
  entityQueryKey: (id: string) => ['backer-rewards', 'viewer', 'unbound', id] as readonly unknown[],
  entityStore: { resource: 'backer-rewards', scopeArg: 'rewardScope' } as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetMarketBackerEarningsRow>;

function _useGetMarketBackerEarningsPerEntitySubjects(args: useGetMarketBackerEarningsArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.trade.v0`, `morfi.market.${args.pubkey}.backer_fee_claim.v0`, `morfi.market.${args.pubkey}.user.*.backing.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketBackerEarningsApplyEvent(
  prev: GetMarketBackerEarningsRow | null | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketBackerEarningsArgs,
): GetMarketBackerEarningsRow | null | undefined {
  // A null row (e.g. a 401 → anonymous result) has nothing to patch.
  if (prev === undefined || prev === null) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetMarketBackerEarningsRow = prev;
    // subject "morfi.market.${m}.user.*.trade.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['baseFeeToBackersD6']) ?? ((raw as Record<string, unknown> | undefined)?.['baseFeeToBackersD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['vaultD6'];
            next = { ...next, vaultD6: addD6String(_prevField, _v) } as GetMarketBackerEarningsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['baseFeeToBackersD6']) ?? ((raw as Record<string, unknown> | undefined)?.['baseFeeToBackersD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['totalCollectedD6'];
            next = { ...next, totalCollectedD6: addD6String(_prevField, _v) } as GetMarketBackerEarningsRow;
          }
        }
        next = (HELPERS.recomputeBackerEarnings as unknown as (prev: GetMarketBackerEarningsRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketBackerEarningsRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.market.${m}.backer_fee_claim.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.backer_fee_claim\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(BackerFeeClaimEventSchema, _d)
            : fromBinary(BackerFeeClaimEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof BackerFeeClaimEventSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['vaultBalanceAfterD6']) ?? ((raw as Record<string, unknown> | undefined)?.['vaultBalanceAfterD6']));
          if (_v !== undefined) {
            next = { ...next, vaultD6: String(_v) } as GetMarketBackerEarningsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['supplyAfterD6']) ?? ((raw as Record<string, unknown> | undefined)?.['supplyAfterD6']));
          if (_v !== undefined) {
            next = { ...next, supplyD6: String(_v) } as GetMarketBackerEarningsRow;
          }
        }
        next = (HELPERS.applyClaimToBackerEarnings as unknown as (prev: GetMarketBackerEarningsRow, raw: ReturnType<typeof fromBinary<typeof BackerFeeClaimEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketBackerEarningsRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
        next = (HELPERS.recomputeBackerEarnings as unknown as (prev: GetMarketBackerEarningsRow, raw: ReturnType<typeof fromBinary<typeof BackerFeeClaimEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketBackerEarningsRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.market.${m}.user.*.backing.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.backing\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBackingSchema, _d)
            : fromBinary(MarketBackingSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBackingSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['sharesDeltaD6']) ?? ((raw as Record<string, unknown> | undefined)?.['sharesDeltaD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['supplyD6'];
            next = { ...next, supplyD6: addD6String(_prevField, _v) } as GetMarketBackerEarningsRow;
          }
        }
        next = (HELPERS.applyBackingToBackerEarnings as unknown as (prev: GetMarketBackerEarningsRow, raw: ReturnType<typeof fromBinary<typeof MarketBackingSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketBackerEarningsRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
        next = (HELPERS.recomputeBackerEarnings as unknown as (prev: GetMarketBackerEarningsRow, raw: ReturnType<typeof fromBinary<typeof MarketBackingSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketBackerEarningsRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  return next;
}

export const useGetMarketBackerEarningsRealtime = {
  domain: 'backer-earnings' as const,
  domainPins: [] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetMarketBackerEarningsArgs) => _useGetMarketBackerEarningsPerEntitySubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  applyEvent:
    (args: useGetMarketBackerEarningsArgs) =>
    (prev: GetMarketBackerEarningsRow | null | undefined, ev: MorfiEvent<unknown>): GetMarketBackerEarningsRow | null | undefined =>
      _useGetMarketBackerEarningsApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetMarketBackerEarningsRow, unknown>;
